import "web-ui/tom-select"
import { Controller } from "web-ui/controller"
import * as Log from "web-ui/log"

// Connects to data-controller="combo-box"
export default class extends Controller {
  static values = {
    searchPlaceholder: { type: String, default: "Search..." },
    addOptionUrl: { type: String },
    addOptionValue: { type: String },
    clearButtonEnabled: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false }
  }

  static events = {
    "turbo:before-cache@document": "beforeTurboCache",
    "pageshow@window": "pageShow"
  }

  connect() {
    this.debug("Connect")

    this.select = this.element.querySelector("select")
    this.select.addEventListener("morphdom:updated", this.selectMorphed)

    this.dialog = this.select.closest("dialog")

    this.initializeTomSelect()

    if (this.autofocusValue) {
      requestAnimationFrame(() => {
        if (!this.element.isConnected) {
          return
        }

        this.tomSelect.focus()
      })
    }
  }

  initializeTomSelect(plugins) {
    plugins ??= this.plugins()

    const select = this.select

    this.debug("Initialize TomSelect", { selectId: select.id, searchPlaceholder: this.searchPlaceholderValue })

    if (this.tomSelect) {
      this.tomSelect.destroy()
    }

    const hasBlankOption = select.querySelectorAll("option[value='']").length > 0

    const body = document.querySelector("body")
    let dropdownParent = body

    if (this.dialog) {
      dropdownParent = this.dialog
    }

    this.tomSelect = new TomSelect(select, {
      plugins,
      refreshThrottle: 0,
      dropdownParent,
      openOnFocus: false,
      allowEmptyOption: hasBlankOption,
      maxOptions: null,

      render: {
        dropdown: () => {
          return `<div id="${select.id}-dropdown"></div>`
        },
        item: (data, escape) => {
          return `<div title="${escape(data.text)}">${escape(data.text)}</div>`
        }
      }
    })
  }

  plugins() {
    let dropdownPlacementStrategy = "absolute"

    if (this.dialog) {
      dropdownPlacementStrategy = "fixed"
    }

    const plugins = {
      "no_backspace_delete": {},
      "dropdown_input": { placeholder: this.searchPlaceholderValue },
      "dropdown_placement": { strategy: dropdownPlacementStrategy },
      "dropdown_scroll": {},
      "dropdown_open": {},
      "dropdown_close_on_scroll": {},
      "turbo_morph_sync": {},
      "submit_form": {},
      "destroy": {}
    }

    if (this.clearButtonEnabledValue) {
      plugins["clear_button"] = {}
    }

    if (this.select.multiple) {
      plugins["checklist_options"] = {}
      plugins["remove_button"] = {}
    }

    if (this.addOptionUrlValue) {
      plugins["add_option"] = {
        url: this.addOptionUrlValue,
        value: this.addOptionValueValue
      }
    }

    return plugins
  }

  selectMorphed = () => {
    const plugins = this.plugins()

    if (this.pluginsChanged(plugins)) {
      this.debug("Plugins Changed", { pluginNames: Object.keys(plugins) })

      this.initializeTomSelect(plugins)
      return
    }

    const loadedPlugins = this.tomSelect.plugins.loaded

    loadedPlugins["dropdown_input"].setPlaceholder(this.searchPlaceholderValue)

    if (this.addOptionUrlValue) {
      loadedPlugins["add_option"].configure({
        url: this.addOptionUrlValue,
        value: this.addOptionValueValue
      })
    }
  }

  pluginsChanged(plugins) {
    const pluginNames = Object.keys(plugins)
    const loadedPluginNames = this.tomSelect.plugins.names

    if (pluginNames.length !== loadedPluginNames.length) {
      return true
    }

    return !pluginNames.every((pluginName) => loadedPluginNames.includes(pluginName))
  }

  addOption = (value, text) => {
    const option = this.tomSelect.options[value]

    if (!option) {
      this.tomSelect.addOption({ value, text }, true)
      return
    }

    if (option.text !== text) {
      option.text = text
      this.tomSelect.updateOption(value, option)
    }
  }

  selectOption = (value) => {
    this.tomSelect.addItem(value)
  }

  beforeTurboCache() {
    this.debug("Before Turbo Cache")

    this.tomSelect.destroy()
    this.destroyedForTurboCache = true
  }

  pageShow(event) {
    if (!event.persisted || !this.destroyedForTurboCache) {
      return
    }

    this.debug("Page Show From Back/Forward Cache")

    this.destroyedForTurboCache = false

    this.initializeTomSelect()
  }

  disconnect() {
    this.debug("Disconnect")

    this.select.removeEventListener("morphdom:updated", this.selectMorphed)

    this.tomSelect.destroy()
  }

  debug(message, data={}) {
    Object.assign(data, {
      id: this.element.id
    })

    Log.debug("Combo Box - " + message, data)
  }
}
